import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { chatEventsSelector } from '@/state/session';
export const useChatScroll = () => {
    const messagesRef = useRef(null);
    const bodyRef = useRef(null);
    const isScrollingRef = useRef(false);
    const scrollContentTimeoutRef = useRef(null);
    const scrollThrottleTimeoutRef = useRef(null);
    const [hasScrollableContent, setHasScrollableContent] = useState(false);
    const [whitespaceHeight, setWhitespaceHeight] = useState(0);
    const initialScroll = useRef(false);
    const chatEvents = useRecoilValue(chatEventsSelector);
    //Check if the last message is a user message
    const isLastMessageUserMessage = useMemo(() => {
        var _a;
        return ((_a = chatEvents[chatEvents.length - 1]) === null || _a === void 0 ? void 0 : _a.event_type) === 'user_message';
    }, [chatEvents]);
    const calculateWhitespaceHeight = useCallback(() => {
        const container = messagesRef.current;
        if (!container) {
            return;
        }
        // Get all messages in order
        const allMessages = Array.from(container.querySelectorAll('[data-event-type]'));
        if (allMessages.length === 0) {
            setWhitespaceHeight(0);
            return;
        }
        // Find the last user message
        let lastUserMessageIndex = -1;
        for (let i = allMessages.length - 1; i >= 0; i--) {
            if (allMessages[i].getAttribute('data-event-type') === 'user_message') {
                lastUserMessageIndex = i;
                break;
            }
        }
        // Get non-user messages that come after the last user message
        const nonUserMessagesAfterLastUser = allMessages
            .slice(lastUserMessageIndex + 1)
            .filter((message) => message.getAttribute('data-event-type') !== 'user_message');
        if (nonUserMessagesAfterLastUser.length === 0) {
            setWhitespaceHeight(0);
            return;
        }
        // Calculate total height of non-user messages after last user message
        let totalMessageHeight = 0;
        nonUserMessagesAfterLastUser.forEach((message) => {
            totalMessageHeight += message.offsetHeight;
        });
        setWhitespaceHeight(totalMessageHeight);
    }, []);
    const checkScrollableContent = useCallback(() => {
        const el = messagesRef.current;
        if (!el) {
            return;
        }
        // Clear any existing timeout
        if (scrollContentTimeoutRef.current) {
            window.clearTimeout(scrollContentTimeoutRef.current);
        }
        // Debounce the check for 100ms
        scrollContentTimeoutRef.current = window.setTimeout(() => {
            const container = messagesRef.current;
            if (container) {
                const allMessages = container.querySelectorAll(isLastMessageUserMessage
                    ? '[data-event-type]' // Include all messages if last is user message
                    : '[data-event-type]:not([data-event-type="user_message"])' // Only non-user messages
                );
                if (allMessages.length === 0) {
                    setHasScrollableContent(false);
                    return;
                }
                const lastMessageElement = allMessages[allMessages.length - 1];
                // Calculate the position of the bottom of the last message
                const lastMessageBottom = lastMessageElement.offsetTop + lastMessageElement.offsetHeight;
                // Add some padding (same as in calculateWhitespaceHeight)
                const paddingAfterLastMessage = 50;
                // Check if the last message's bottom (plus padding) is below the current viewport
                const currentViewportBottom = container.scrollTop + container.clientHeight;
                const hasMore = lastMessageBottom + paddingAfterLastMessage >
                    currentViewportBottom + 40;
                setHasScrollableContent(hasMore);
            }
        }, 100);
    }, [isLastMessageUserMessage]);
    const scrollToBottom = () => {
        const el = messagesRef.current;
        if (!el || isScrollingRef.current) {
            return;
        }
        isScrollingRef.current = true;
        el.scrollTo({
            top: el.scrollHeight,
            behavior: 'smooth',
        });
        // Reset the scrolling flag after animation completes
        setTimeout(() => {
            isScrollingRef.current = false;
            setHasScrollableContent(false);
        }, 300);
    };
    // Check if there's scrollable content below
    useEffect(() => {
        const el = messagesRef.current;
        if (!el) {
            return;
        }
        checkScrollableContent();
        const handleScroll = () => {
            // Throttle expensive DOM queries to at most once per 50ms
            if (!scrollThrottleTimeoutRef.current) {
                checkScrollableContent();
                scrollThrottleTimeoutRef.current = window.setTimeout(() => {
                    scrollThrottleTimeoutRef.current = null;
                }, 50);
            }
        };
        // Add scroll event listener
        el.addEventListener('scroll', handleScroll);
        return () => {
            // Clean up event listener and any pending timeout
            el.removeEventListener('scroll', handleScroll);
            if (scrollContentTimeoutRef.current) {
                window.clearTimeout(scrollContentTimeoutRef.current);
            }
            if (scrollThrottleTimeoutRef.current) {
                window.clearTimeout(scrollThrottleTimeoutRef.current);
            }
        };
    }, [checkScrollableContent]);
    const scrollToElementRef = useRef(null);
    const scrollToElement = useCallback((element, offset = 0) => {
        const container = messagesRef.current;
        if (!container || !element) {
            return;
        }
        if (isScrollingRef.current) {
            // If already scrolling, queue another attempt
            setTimeout(() => { var _a; return (_a = scrollToElementRef.current) === null || _a === void 0 ? void 0 : _a.call(scrollToElementRef, element, offset); }, 100);
            return;
        }
        isScrollingRef.current = true;
        // Get the element's position relative to the container
        const containerRect = container.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        // Calculate the scroll position properly by finding the element's position relative to the container
        const relativePosition = elementRect.top - containerRect.top;
        const scrollTop = container.scrollTop + relativePosition - offset;
        container.scrollTo({
            top: scrollTop,
            behavior: 'smooth',
        });
        setTimeout(() => {
            isScrollingRef.current = false;
        }, 100);
    }, []);
    // Keep the ref updated with the latest function
    useEffect(() => {
        scrollToElementRef.current = scrollToElement;
    }, [scrollToElement]);
    // Simple state-based scroll: only scroll for user messages
    useEffect(() => {
        const lastEvent = chatEvents[chatEvents.length - 1];
        if ((lastEvent === null || lastEvent === void 0 ? void 0 : lastEvent.event_type) === 'user_message') {
            const isFirstMessage = !initialScroll.current;
            initialScroll.current = true;
            // Small delay to ensure DOM is rendered
            const timeoutId = setTimeout(() => {
                var _a;
                // First message: stay in natural position
                if (isFirstMessage) {
                    return;
                }
                // Find the last user message element and scroll to it
                const userMessages = (_a = messagesRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll('[data-event-type="user_message"]');
                if (userMessages && userMessages.length > 0) {
                    const lastUserMessage = userMessages[userMessages.length - 1];
                    scrollToElement(lastUserMessage, 80);
                }
            }, 100);
            // Cleanup timeout on unmount or dependency change
            return () => {
                clearTimeout(timeoutId);
            };
        }
        // Update whitespace and scroll indicators for all events with a small delay
        // to ensure DOM is ready
        const updateTimeoutId = setTimeout(() => {
            calculateWhitespaceHeight();
            checkScrollableContent();
        }, 50);
        return () => {
            clearTimeout(updateTimeoutId);
        };
    }, [
        chatEvents,
        calculateWhitespaceHeight,
        scrollToElement,
        checkScrollableContent,
    ]);
    return {
        messagesRef,
        bodyRef,
        hasScrollableContent,
        scrollToBottom,
        whitespaceHeight,
    };
};
